/**
 * Per-content page presentation, stored as a JSON string in contents.page_style
 * (authored in Diapason's content editor). Parsing is tolerant like parseThemeTokens:
 * anything malformed degrades to null (= app default), never throws.
 *
 * Background modes:
 *  - default:      the theme's page background (what every page shows today)
 *  - surface:      the theme's surface tone (soft card tint over the whole page)
 *  - primary-soft: a translucent wash of the chrome color — stays on-brand and
 *                  updates itself when the study theme changes
 *  - custom:       an explicit hex the researcher picked (frozen; contrast-checked
 *                  at authoring time in Diapason)
 */

export type PageBackgroundMode = "default" | "surface" | "primary-soft" | "gradient" | "custom";

export interface PageStyle {
  background: { mode: PageBackgroundMode; hex?: string };
}

const MODES: PageBackgroundMode[] = ["default", "surface", "primary-soft", "gradient", "custom"];
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const parsePageStyle = (value?: string | null): PageStyle | null => {
  if (typeof value !== "string" || value.trim() === "") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const background = (parsed as { background?: unknown }).background;
  if (typeof background !== "object" || background === null) return null;

  const { mode, hex } = background as { mode?: unknown; hex?: unknown };
  if (typeof mode !== "string" || !MODES.includes(mode as PageBackgroundMode)) return null;
  if (mode === "custom" && (typeof hex !== "string" || !HEX_COLOR_RE.test(hex.trim()))) return null;

  return {
    background: {
      mode: mode as PageBackgroundMode,
      ...(mode === "custom" ? { hex: (hex as string).trim() } : {}),
    },
  };
};

/**
 * CSS background value for a page — theme-derived modes are pure CSS-var expressions.
 *
 * Every mode resolves to an OPAQUE composite: translucent washes are layered over
 * their own theme-background base (`background: <wash-layer>, <base-color>`). A bare
 * rgba() value looked right on a settled page (the white app root sat behind it) but
 * let the LEAVING page show through during Ionic route transitions.
 */
export const resolvePageBackground = (style: PageStyle | null): string => {
  const base = "var(--app-color-background, white)";
  switch (style?.background.mode) {
    case "surface":
      return "var(--app-color-surface, #ffffff)";
    case "primary-soft":
      return `linear-gradient(rgba(var(--app-color-primary-rgb, 171, 240, 239), 0.16), rgba(var(--app-color-primary-rgb, 171, 240, 239), 0.16)), ${base}`;
    case "gradient":
      // Soft chrome-colored wash fading into the theme background — follows the
      // study theme wherever it goes. No `fixed` attachment: it paints on the
      // non-scrolling page frame (IonContent scrolls inside it), and fixed
      // attachment is an iOS-Safari repaint hazard.
      return `linear-gradient(180deg, rgba(var(--app-color-primary-rgb, 171, 240, 239), 0.30) 0%, var(--app-color-background, white) 55%), ${base}`;
    case "custom":
      return style.background.hex ?? base;
    default:
      return base;
  }
};

/** One-step convenience for content consumers. */
export const pageBackgroundOf = (pageStyleJson?: string | null): string =>
  resolvePageBackground(parsePageStyle(pageStyleJson));
